import React from "react";

import { TableBodyProps, TableCellProps, TableContainerProps, TableProps } from "@portex-pro/ui-components";
import clsx from "clsx";

export type TableViewColumnV2<T> = {
  key: string;
  title: string;
  renderCell: (item: T) => React.ReactNode;
  cellProps?: React.TdHTMLAttributes<HTMLTableCellElement>;
  headerCellProps?: React.ThHTMLAttributes<HTMLTableCellElement>;
  cellClassName?: string;
  headerClassName?: string;
};

export type TableViewColumns<T> = {
  name: string | (() => React.ReactElement);
  renderCell: (item: T, rowIndex: number) => React.ReactNode;
  cellProps?: TableCellProps | ((item: T, rowIndex: number) => TableCellProps);
  headerCellProps?: TableCellProps;
  hideCell?: boolean;
}[];

type ExtraBodyProps = {
  allWhite?: boolean;
};

export type TableViewRows<T> = {
  renderRowStart?: (item: T, rowIndex: number) => React.ReactElement | null;
  renderRowStartCellProps?: TableCellProps | ((item: T, rowIndex: number) => TableCellProps);
  renderRowEnd?: (item: T, rowIndex: number) => React.ReactElement | null;
  renderRowEndCellProps?: TableCellProps | ((item: T, rowIndex: number) => TableCellProps);
  endingIcon?: (item: T, rowIndex: number) => React.ReactElement;
  headerEndingIcon?: React.ReactElement;
  endingIconHeaderCellProps?: TableCellProps;
  endingIconCellProps?: TableCellProps;
  startingIcon?: (item: T, rowIndex: number) => React.ReactElement;
  headerStartingIcon?: React.ReactElement;
  startingIconHeaderCellProps?: TableCellProps;
  startingIconCellProps?: TableCellProps | ((item: T) => TableCellProps);
  onClickRow?: (item: T, rowIndex: number) => void;
  rowBodyProps?: (TableBodyProps & ExtraBodyProps) | ((item: T, rowIndex: number) => TableBodyProps & ExtraBodyProps);
  headerCellProps?: TableCellProps;
  cellProps?: TableCellProps | ((item: T, rowIndex: number) => TableCellProps);
  greyRowSpacing?: boolean | number;
};

export interface TableViewV2Props<T> {
  items: T[];
  columns: TableViewColumnV2<T>[];
  isLoading?: boolean;
  headerStartingIcon?: React.ReactNode;
  startingIcon?: (item: T) => React.ReactNode;
  startingIconHeaderCellProps?: React.ThHTMLAttributes<HTMLTableCellElement>;
  startingIconCellProps?: (item: T) => React.TdHTMLAttributes<HTMLTableCellElement>;
  onClickRow?: (item: T) => void;
  rowKey: keyof T | ((item: T) => string | number);
  isRowSelected?: (item: T) => boolean;
}

export interface TableViewProps<T> {
  items: T[];
  isLoading?: boolean;
  columns: TableViewColumns<T>;
  rows?: TableViewRows<T>;
  hideTableHead?: boolean;
  className?: string;
  tableProps?: TableProps;
  tableContainerProps?: TableContainerProps;
}

const LoadingSpinner: React.FC = () => (
  <div className="flex items-center justify-center h-32">
    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
    <span className="ml-2 text-gray-600">Loading...</span>
  </div>
);

export function TableViewV2<T>(props: TableViewV2Props<T>): React.ReactElement {
  const {
    items = [],
    columns = [],
    isLoading,
    headerStartingIcon,
    startingIcon,
    startingIconHeaderCellProps,
    startingIconCellProps,
    onClickRow,
    rowKey,
    isRowSelected,
  } = props;

  const getRowKeyValue = (item: T): string | number => {
    if (typeof rowKey === "function") {
      return rowKey(item);
    }
    return item[rowKey as keyof T] as string | number;
  };

  const headerCellStyle: React.CSSProperties = {
    fontFamily:
      '"Helvetica Neue", Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
    fontWeight: 700,
    fontSize: "13px",
    lineHeight: "24px",
    letterSpacing: "0%",
    verticalAlign: "middle",
    paddingTop: "8.65px",
    paddingRight: "16px",
    paddingBottom: "9.32px",
    paddingLeft: "8px",
    textAlign: "left",
    color: "#708093",
  };

  const dataCellStyle: React.CSSProperties = {
    fontFamily:
      '"Helvetica Neue", Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
    paddingTop: "8.65px",
    paddingRight: "16px",
    paddingBottom: "9.32px",
    paddingLeft: "8px",
    verticalAlign: "top",
  };

  return (
    <div className="relative overflow-auto" style={{ maxHeight: "inherit" }}>
      <table className="min-w-full table-fixed">
        <thead className="bg-gray-50 sticky top-0 z-10">
          <tr>
            {headerStartingIcon && (
              <th
                scope="col"
                {...startingIconHeaderCellProps}
                style={{ ...headerCellStyle, ...startingIconHeaderCellProps?.style }}
                className={clsx(
                  "border-b border-gray-200 uppercase bg-gray-50",
                  startingIconHeaderCellProps?.className
                )}
              >
                {headerStartingIcon}
              </th>
            )}
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                {...column.headerCellProps}
                style={{ ...headerCellStyle, ...column.headerCellProps?.style }}
                className={clsx(
                  "border-b border-gray-200 uppercase bg-gray-50",
                  column.headerClassName,
                  column.headerCellProps?.className
                )}
              >
                {column.title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white">
          {isLoading ? (
            <tr>
              <td colSpan={columns.length + (headerStartingIcon ? 1 : 0)} className="p-4 text-center text-gray-500">
                <LoadingSpinner />
              </td>
            </tr>
          ) : items.length === 0 ? (
            <tr>
              <td colSpan={columns.length + (headerStartingIcon ? 1 : 0)} className="p-4 text-center text-gray-500">
                No data available.
              </td>
            </tr>
          ) : (
            items.map((item) => {
              const isSelected = isRowSelected ? isRowSelected(item) : false;
              const sIconCellProps = startingIconCellProps ? startingIconCellProps(item) : {};
              return (
                <tr
                  key={getRowKeyValue(item)}
                  onClick={(e) => {
                    if (e.target instanceof HTMLElement) {
                      const targetNode = e.target as HTMLElement;
                      if (targetNode.closest('input[type="checkbox"]') || targetNode.closest("label")) {
                        return;
                      }
                    }
                    onClickRow?.(item);
                  }}
                  className={clsx(
                    onClickRow ? "cursor-pointer" : "",
                    isSelected ? "bg-blue-50" : "bg-white hover:bg-gray-50"
                  )}
                >
                  {startingIcon && (
                    <td
                      {...sIconCellProps}
                      style={{ ...dataCellStyle, verticalAlign: "middle", ...sIconCellProps?.style }}
                      className={clsx("text-sm", sIconCellProps?.className)}
                    >
                      {startingIcon(item)}
                    </td>
                  )}
                  {columns.map((column) => {
                    const cellProps = column.cellProps || {};
                    return (
                      <td
                        key={column.key}
                        {...cellProps}
                        style={{ ...dataCellStyle, ...cellProps?.style }}
                        className={clsx("text-sm", column.cellClassName, cellProps?.className)}
                      >
                        {column.renderCell(item)}
                      </td>
                    );
                  })}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

// Legacy TableView component for backwards compatibility
const TableView = <T,>(props: TableViewProps<T>): JSX.Element => {
  const { items, isLoading, columns, rows } = props;

  const v2Columns: TableViewColumnV2<T>[] = columns.map((col, index) => ({
    key: `col-${index}`,
    title: typeof col.name === "string" ? col.name : "",
    renderCell: (item: T) => col.renderCell(item, 0),
    cellClassName: col.hideCell ? "hidden" : "",
    headerClassName: col.hideCell ? "hidden" : "",
  }));

  return (
    <TableViewV2
      items={items}
      columns={v2Columns}
      isLoading={isLoading}
      headerStartingIcon={rows?.headerStartingIcon}
      startingIcon={rows?.startingIcon ? (item) => rows?.startingIcon?.(item, 0) || <></> : undefined}
      startingIconHeaderCellProps={undefined}
      startingIconCellProps={undefined}
      onClickRow={rows?.onClickRow ? (item) => rows?.onClickRow?.(item, 0) : undefined}
      rowKey={(item) => items.indexOf(item).toString()}
      isRowSelected={() => false}
    />
  );
};

export default TableView;
